import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User as UserIcon, Edit, Share2, MessageCircle, Calendar, MapPin, Link as LinkIcon, Settings, Grid, List, ArrowLeft, Crown, Shield, Eye, Bookmark, Users, Save, Flag, AlertTriangle, ShoppingBag, Truck, CheckCircle, Download } from 'lucide-react';
import { User } from '../types';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { profileService } from '../services/profileService';
import { collectionsService } from '../services/collectionsService';
import { followsService } from '../services/followsService';
import { paymentsService } from '../services/paymentsService';
import { supabase } from '../lib/supabase';
import { ArtCategory } from '../types';
import PostCard from './PostCard';
import FollowButton from './FollowButton';
import SettingsModal from './SettingsModal';
import ReportDialog from './ReportDialog';
import { reportsService } from '../services/reportsService';
import { ContentModerationError } from '../lib/contentModeration';

interface ProfileViewProps {
  onCreatePost?: () => void;
}

const ProfileView: React.FC<ProfileViewProps> = ({ onCreatePost }) => {
  const { user, refreshUser } = useAuth();
  const { select, locale, t } = useLanguage();
  const { username: urlUsername } = useParams<{ username: string }>();
  const navigate = useNavigate();

  const categoryLabels: Record<ArtCategory, string> = {
    music: select({ es: 'Música', en: 'Music' }),
    writing: select({ es: 'Escritura', en: 'Writing' }),
    tattoo: select({ es: 'Tatuajes', en: 'Tattoos' }),
    digital_design: select({ es: 'Diseño Digital', en: 'Digital Design' }),
    fashion_design: select({ es: 'Diseño de Moda', en: 'Fashion Design' }),
    photography: select({ es: 'Fotografía', en: 'Photography' }),
    sculpture: select({ es: 'Escultura', en: 'Escultura' }),
    painting: select({ es: 'Pintura', en: 'Painting' }),
    dance: select({ es: 'Danza', en: 'Danza' }),
    filmmaking: select({ es: 'Cine', en: 'Filmmaking' }),
    makeup: select({ es: 'Maquillaje', en: 'Maquillaje' }),
  };
  const [profile, setProfile] = useState<User | null>(null);
  const [posts, setPosts] = useState<Record<string, unknown>[]>([]);
  const [collections, setCollections] = useState<Record<string, unknown>[]>([]);
  const [orders, setOrders] = useState<Record<string, unknown>[]>([]);
  const [followers, setFollowers] = useState<User[]>([]);
  const [following, setFollowing] = useState<User[]>([]);
  const [stats, setStats] = useState({
    posts: 0,
    likes: 0,
    collaborations: 0,
    followers: 0,
    following: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'posts' | 'collections' | 'orders' | 'followers' | 'following'>('posts');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: '',
    bio: '',
    location: '',
    website: '',
    art_categories: [] as ArtCategory[],
  });
  const [showReportModal, setShowReportModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const selectedFilesMessage = React.useMemo(() => {
    if (avatarFile && bannerFile) {
      return select({ es: 'Avatar y banner seleccionados', en: 'Avatar and banner selected' });
    }
    if (avatarFile) {
      return select({ es: 'Avatar seleccionado', en: 'Avatar selected' });
    }
    if (bannerFile) {
      return select({ es: 'Banner seleccionado', en: 'Banner selected' });
    }
    return '';
  }, [avatarFile, bannerFile, select]);

  // Determine if viewing own profile or another user's profile
  const isOwnProfile = !urlUsername || urlUsername === user?.username;
  const isModerator = user?.user_type === 'moderator' || user?.user_type === 'admin';
  const profileUser = isOwnProfile ? user : profile;

  useEffect(() => {
    void fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlUsername, user]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError('');
      setFeedbackMessage('');

      let targetProfile;

      if (isOwnProfile) {
        targetProfile = user;
      } else if (urlUsername) {
        targetProfile = await profileService.getProfileByUsername(urlUsername);
      } else {
        // This case should ideally not happen if routing is set up correctly
        // but as a fallback, try to get current user's profile
        targetProfile = user;
      }

      if (!targetProfile) {
        setError(select({ es: 'Perfil no encontrado', en: 'Profile not found' }));
        return;
      }

      setProfile(targetProfile);
      setEditForm({
        full_name: targetProfile.full_name || '',
        bio: targetProfile.bio || '',
        location: targetProfile.location || '',
        website: targetProfile.website || '',
        art_categories: targetProfile.art_categories || [],
      });

      // Load profile data in parallel
      const targetUserId = targetProfile.id;
      const [
        userPosts,
        userStats,
        collaborationsCount,
        userCollections,
        userFollowers,
        userFollowing,
        followCounts,
        userOrders
      ] = await Promise.all([
        profileService.getUserPosts(targetUserId, {
          includeAll: isOwnProfile || isModerator,
          moderatorView: isModerator,
          viewerId: user?.id || null,
        }),
        profileService.getUserStats(targetUserId),
        getCollaborationsCount(targetUserId),
        isOwnProfile ? collectionsService.getUserCollections(targetUserId).catch(() => []) : Promise.resolve([]),
        followsService.getFollowers(targetUserId).catch(() => []),
        isOwnProfile ? followsService.getFollowing(targetUserId).catch(() => []) : Promise.resolve([]),
        followsService.getFollowCounts(targetUserId),
        isOwnProfile ? paymentsService.getOrders(true).catch(() => []) : Promise.resolve([])
      ]);

      setPosts(userPosts || []);
      setCollections(userCollections || []);
      setOrders(userOrders || []);
      setFollowers(userFollowers || []);
      setFollowing(userFollowing || []);

      setStats({
        posts: userStats?.posts || 0,
        likes: userStats?.likes || 0,
        collaborations: collaborationsCount,
        followers: followCounts?.followers || 0,
        following: followCounts?.following || 0,
      });

    } catch (err) {
      console.error('Error fetching profile data:', err);
      setError(select({ es: 'Error al cargar el perfil', en: 'Failed to load profile' }));
      // Set empty arrays as fallback
      setPosts([]);
      setCollections([]);
      setFollowers([]);
      setFollowing([]);
      setStats({
        posts: 0,
        likes: 0,
        collaborations: 0,
        followers: 0,
        following: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const getCollaborationsCount = async (targetUserId: string) => {
    try {
      // First get authored collaborations count
      const { count: authoredCount } = await supabase
        .from('collaborations')
        .select('id', { count: 'exact', head: true })
        .eq('author_id', targetUserId);

      // Then get accepted applications count
      const { count: acceptedCount } = await supabase
        .from('collaboration_applications')
        .select('id', { count: 'exact', head: true })
        .eq('applicant_id', targetUserId)
        .eq('status', 'accepted');

      return (authoredCount || 0) + (acceptedCount || 0);
    } catch (err) {
      console.error('Error loading collaborations count:', err);
      return 0;
    }
  };

  const handleReportProfileSubmit = async ({ reason, description }: { reason: string; description?: string }) => {
    const targetProfileId = profileUser?.id || user?.id;
    if (!targetProfileId) return;

    try {
      await reportsService.createReport({
        itemType: 'profile',
        itemId: targetProfileId,
        reason,
        description,
      });
      setShowReportModal(false);
      setFeedbackMessage(select({
        es: 'Reporte enviado. Nuestro equipo revisará el perfil.',
        en: 'Report sent. Our team will review the profile.',
      }));
      setTimeout(() => setFeedbackMessage(''), 4000);
    } catch (err: unknown) {
      console.error('Error reporting profile:', err);
      const message =
        err instanceof Error
          ? err.message
          : select({
              es: 'No se pudo enviar el reporte del perfil.',
              en: 'Could not send the profile report.',
            });
      setFeedbackMessage(message);
      setTimeout(() => setFeedbackMessage(''), 5000);
    }
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    // Reset file states when starting edit
    setAvatarFile(null);
    setBannerFile(null);
    setAvatarPreview(null);
    setBannerPreview(null);
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(select({ es: 'Por favor selecciona una imagen válida', en: 'Please select a valid image file' }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(select({ es: 'La imagen no puede superar los 5MB', en: 'Image size cannot exceed 5MB' }));
      return;
    }

    setAvatarFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(select({ es: 'Por favor selecciona una imagen válida', en: 'Please select a valid image file' }));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError(select({ es: 'La imagen no puede superar los 10MB', en: 'Image size cannot exceed 10MB' }));
      return;
    }

    setBannerFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setBannerPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    try {
      if (!user) return;

      setUploadingImages(true);
      setUploadingImages(true);
      let avatarUrl = profileUser?.avatar_url;
      let bannerUrl = profileUser?.banner_url;

      // Upload avatar if selected
      if (avatarFile) {
        avatarUrl = await profileService.uploadAvatarImage(avatarFile, user.id);
      }

      // Upload banner if selected
      if (bannerFile) {
        bannerUrl = await profileService.uploadBannerImage(bannerFile, user.id);
      }

      await profileService.updateProfile(user.id, {
        ...editForm,
        avatar_url: avatarUrl,
        banner_url: bannerUrl,
      });

      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }

      setIsEditing(false);
      await fetchProfileData();

      // Clear file states
      setAvatarFile(null);
      setBannerFile(null);
      setAvatarPreview(null);
      setBannerPreview(null);
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setError(t(key, 'Your content violates our community guidelines.'));
      } else {
        setError(select({ es: 'Error al actualizar el perfil', en: 'Failed to update profile' }));
        console.error('Error updating profile:', err);
      }
    } finally {
      setUploadingImages(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setAvatarFile(null);
    setBannerFile(null);
    setAvatarPreview(null);
    setBannerPreview(null);
    setEditForm({
      full_name: profileUser?.full_name || '',
      bio: profileUser?.bio || '',
      location: profileUser?.location || '',
      website: profileUser?.website || '',
      art_categories: profileUser?.art_categories || [],
    });
  };

  const toggleArtCategory = (category: ArtCategory) => {
    setEditForm(prev => ({
      ...prev,
      art_categories: prev.art_categories.includes(category)
        ? prev.art_categories.filter(c => c !== category)
        : [...prev.art_categories, category]
    }));
  };

  const handleLike = async (postId: string) => {
    // Handle like functionality here
    console.log('Liked post:', postId);
  };

  const handleSave = async (postId: string) => {
    // Handle save functionality here
    console.log('Saved post:', postId);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="animate-pulse space-y-6">
          {/* Header skeleton */}
          <div className="h-48 bg-gray-200 rounded-xl"></div>
          <div className="h-32 bg-gray-200 rounded-xl"></div>
          {/* Content skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <h2 className="text-xl font-bold text-red-900 mb-2">
            {select({ es: 'Error', en: 'Error' })}
          </h2>
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {select({ es: 'Volver', en: 'Go back' })}
          </button>
        </div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="text-center py-12">
          <UserIcon size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {select({ es: 'Perfil no encontrado', en: 'Profile not found' })}
          </h3>
          <p className="text-gray-500">
            {select({ es: 'Este usuario no existe o no está disponible', en: 'This user does not exist or is unavailable' })}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <ArrowLeft size={20} className="inline-block mr-2" />
            {select({ es: 'Volver', en: 'Go back' })}
          </button>
        </div>
      </div>
    );
  }

  const accountStatusInfo = (() => {
    switch (profileUser.account_status) {
      case 'warned':
        return {
          title: select({ es: 'Cuenta advertida', en: 'Account warned' }),
          message:
            profileUser.moderation_notes ||
            select({
              es: 'Se ha detectado una posible infracción de las normas. Revisa las pautas de la comunidad para evitar sanciones futuras.',
              en: 'We detected a potential policy violation. Review the community guidelines to avoid future penalties.',
            }),
          classes: 'bg-yellow-50 border-yellow-200 text-yellow-800',
          iconColor: 'text-yellow-500',
        };
      case 'suspended':
        return {
          title: select({ es: 'Cuenta suspendida', en: 'Account suspended' }),
          message:
            profileUser.moderation_notes ||
            (profileUser.suspended_until
              ? select({
                es: `El perfil está suspendido hasta el ${new Date(profileUser.suspended_until).toLocaleDateString(locale)}.`,
                en: `This profile is suspended until ${new Date(profileUser.suspended_until).toLocaleDateString(locale)}.`,
              })
              : select({
                es: 'El perfil está temporalmente suspendido mientras se revisa el caso.',
                en: 'The profile is temporarily suspended while the case is reviewed.',
              })),
          classes: 'bg-orange-50 border-orange-200 text-orange-800',
          iconColor: 'text-orange-500',
        };
      case 'banned':
        return {
          title: select({ es: 'Cuenta baneada', en: 'Account banned' }),
          message:
            profileUser.moderation_notes ||
            select({
              es: 'Este perfil ha sido bloqueado permanentemente por incumplir las normas de la comunidad.',
              en: 'This profile has been permanently banned for violating community guidelines.',
            }),
          classes: 'bg-red-50 border-red-200 text-red-800',
          iconColor: 'text-red-500',
        };
      default:
        return null;
    }
  })();

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Back Button for viewing other profiles */}
      {!isOwnProfile && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft size={20} />
          {select({ es: 'Volver', en: 'Back' })}
        </button>
      )}

      {feedbackMessage && (
        <div
          className={`mb-4 px-4 py-3 rounded-xl border text-sm ${feedbackMessage.toLowerCase().includes('no se pudo')
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-green-50 border-green-200 text-green-700'
            }`}
        >
          {feedbackMessage}
        </div>
      )}

      {/* Profile Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        {/* Banner */}
        <div className="h-48 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 relative group">
          {(bannerPreview || profileUser.banner_url) && (
            <img
              src={bannerPreview || profileUser.banner_url}
              alt="Banner"
              className="w-full h-full object-cover transition-all duration-300"
            />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>

          {/* Banner Edit Overlay */}
          {isEditing && (
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <label className="cursor-pointer bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-900 px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all">
                <Edit size={16} />
                Cambiar banner
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerChange}
                  className="hidden"
                />
              </label>
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="p-6 relative">
          <div className="flex flex-col gap-6 -mt-16 md:flex-row md:items-end md:-mt-12">
            {/* Avatar */}
            <div className="relative group shrink-0 self-start md:self-auto">
              <img
                src={avatarPreview || profileUser.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=120&h=120&fit=crop'}
                alt={profileUser.full_name}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg object-cover transition-all duration-300"
              />

              {/* Avatar Edit Overlay */}
              {isEditing && (
                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <label className="cursor-pointer bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-900 p-2 rounded-full transition-all">
                    <Edit size={16} />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                  </label>
                </div>
              )}

              {profileUser.is_premium && (
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                  <Crown className="text-white" size={16} />
                </div>
              )}
              {profileUser.user_type === 'moderator' && (
                <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                  <Shield className="text-white" size={12} />
                </div>
              )}
              {profileUser.user_type === 'admin' && (
                <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                  <Crown className="text-white" size={12} />
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 w-full pt-2 md:pt-0">
              {isEditing ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={editForm.full_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                    className="text-2xl font-bold bg-transparent border-b-2 border-purple-300 focus:border-purple-600 outline-none"
                    placeholder={select({ es: 'Tu nombre completo', en: 'Your full name' })}
                  />
                  <textarea
                    value={editForm.bio}
                    onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg resize-none"
                    rows={3}
                    placeholder={select({ es: 'Cuéntanos sobre ti...', en: 'Tell us about yourself...' })}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={editForm.location}
                      onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder={select({ es: 'Ubicación', en: 'Location' })}
                    />
                    <input
                      type="url"
                      value={editForm.website}
                      onChange={(e) => setEditForm(prev => ({ ...prev, website: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder={select({ es: 'Sitio web', en: 'Website' })}
                    />
                  </div>

                  {/* Art Categories */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Especialidades artísticas', en: 'Art specialties' })}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {Object.entries(categoryLabels).map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleArtCategory(key as ArtCategory)}
                          className={`p-3 text-sm border rounded-lg transition-all text-left touch-target ${editForm.art_categories.includes(key as ArtCategory)
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                            }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveProfile}
                      disabled={uploadingImages}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {uploadingImages ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          {select({ es: 'Subiendo...', en: 'Uploading...' })}
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          {select({ es: 'Guardar', en: 'Save' })}
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {select({ es: 'Cancelar', en: 'Cancel' })}
                    </button>
                  </div>

                  {(avatarFile || bannerFile) && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-700 text-sm">
                        <Eye size={14} />
                        <span>{selectedFilesMessage}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">{profileUser.full_name}</h1>
                    {profileUser.is_premium && (
                      <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-sm font-bold rounded-full shrink-0">
                        {select({ es: '✨ Premium', en: '✨ Premium' })}
                      </span>
                    )}
                  </div>
                  <p className="text-lg text-gray-600 mb-3">@{profileUser.username}</p>

                  {profileUser.bio && (
                    <p className="text-gray-700 mb-4 leading-relaxed">{profileUser.bio}</p>
                  )}

                  {/* Art Categories */}
                  {profileUser.art_categories && profileUser.art_categories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {profileUser.art_categories.map((category: ArtCategory) => (
                        <span
                          key={category}
                          className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                        >
                          {categoryLabels[category]}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    {profileUser.location && (
                      <div className="flex items-center gap-1">
                        <MapPin size={16} />
                        <span>{profileUser.location}</span>
                      </div>
                    )}

                    {profileUser.website && (
                      <div className="flex items-center gap-1">
                        <LinkIcon size={16} />
                        <a
                          href={profileUser.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:text-purple-700 hover:underline"
                        >
                          {select({ es: 'Sitio web', en: 'Website' })}
                        </a>
                      </div>
                    )}

                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      <span>
                        {select({ es: 'Miembro desde', en: 'Member since' })}{' '}
                        {new Date(profileUser.created_at).toLocaleDateString(locale, {
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {accountStatusInfo && (
              <div className={`mt-4 px-4 py-3 rounded-xl border ${accountStatusInfo.classes}`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className={`${accountStatusInfo.iconColor} mt-0.5`} />
                  <div>
                    <h3 className="font-semibold text-sm md:text-base mb-1">{accountStatusInfo.title}</h3>
                    <p className="text-sm leading-relaxed">{accountStatusInfo.message}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {!isEditing && (
              <div className="flex gap-3">
                {isOwnProfile ? (
                  <>
                    <button
                      onClick={handleEditProfile}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Edit size={16} />
                      {select({ es: 'Editar perfil', en: 'Edit profile' })}
                    </button>
                    <button
                      onClick={() => setShowSettingsModal(true)}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Settings size={16} />
                      {select({ es: 'Configuración', en: 'Settings' })}
                    </button>
                  </>
                ) : (
                  <>
                    <FollowButton
                      userId={profileUser.id}
                      username={profileUser.username}
                      size="md"
                      variant="primary"
                    />
                    <button
                      onClick={() => navigate(`/messages/${profileUser.id}`)} // Navigate to messages with user ID
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <MessageCircle size={16} />
                      {select({ es: 'Mensaje', en: 'Message' })}
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                      <Share2 size={16} />
                      {select({ es: 'Compartir', en: 'Share' })}
                    </button>
                    <button
                      onClick={() => setShowReportModal(true)}
                      className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Flag size={16} />
                      {select({ es: 'Reportar', en: 'Report' })}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{formatNumber(stats.posts)}</div>
          <div className="text-sm text-gray-600">{select({ es: 'Publicaciones', en: 'Posts' })}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{formatNumber(stats.likes)}</div>
          <div className="text-sm text-gray-600">{select({ es: 'Me gusta', en: 'Likes' })}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{formatNumber(stats.collaborations)}</div>
          <div className="text-sm text-gray-600">{select({ es: 'Colaboraciones', en: 'Collaborations' })}</div>
        </div>
        <div
          className="bg-white rounded-lg border border-gray-200 p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setActiveTab('followers')}
        >
          <div className="text-2xl font-bold text-gray-900">{formatNumber(stats.followers)}</div>
          <div className="text-sm text-gray-600">{select({ es: 'Seguidores', en: 'Followers' })}</div>
        </div>
        <div
          className="bg-white rounded-lg border border-gray-200 p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setActiveTab('following')}
        >
          <div className="text-2xl font-bold text-gray-900">{formatNumber(stats.following)}</div>
          <div className="text-sm text-gray-600">{select({ es: 'Siguiendo', en: 'Following' })}</div>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <div className="flex items-center justify-between p-4">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('posts')}
                className={`py-2 px-1 border-b-2 transition-colors ${activeTab === 'posts'
                  ? 'border-purple-500 text-purple-600 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                {select({ es: 'Publicaciones', en: 'Posts' })} ({posts.length})
              </button>

              {isOwnProfile && (
                <button
                  onClick={() => setActiveTab('collections')}
                  className={`py-2 px-1 border-b-2 transition-colors ${activeTab === 'collections'
                    ? 'border-purple-500 text-purple-600 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {select({ es: 'Colecciones', en: 'Collections' })} ({collections.length})
                </button>
              )}

              {isOwnProfile && (
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`py-2 px-1 border-b-2 transition-colors ${activeTab === 'orders'
                    ? 'border-purple-500 text-purple-600 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {select({ es: 'Pedidos', en: 'Orders' })} ({orders.length})
                </button>
              )}

              <button
                onClick={() => setActiveTab('followers')}
                className={`py-2 px-1 border-b-2 transition-colors ${activeTab === 'followers'
                  ? 'border-purple-500 text-purple-600 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                {select({ es: 'Seguidores', en: 'Followers' })} ({stats.followers})
              </button>

              {isOwnProfile && (
                <button
                  onClick={() => setActiveTab('following')}
                  className={`py-2 px-1 border-b-2 transition-colors ${activeTab === 'following'
                    ? 'border-purple-500 text-purple-600 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {select({ es: 'Siguiendo', en: 'Following' })} ({stats.following})
                </button>
              )}
            </nav>

            {/* View Mode Toggle */}
            {activeTab === 'posts' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-purple-100 text-purple-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <Grid size={18} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-purple-100 text-purple-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <List size={18} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Posts Tab */}
          {activeTab === 'posts' && (
            <div>
              {posts.length > 0 ? (
                <div className={viewMode === 'grid'
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  : "space-y-6"
                }>
                  {posts.map(post => (
                    <PostCard
                      key={post.id}
                      post={{
                        ...post,
                        author: post.profiles || profileUser,
                        media: post.media_files || [],
                        comments: post.comments || [],
                        likes: post.likes_count || 0,
                        views: post.views_count || 0,
                        createdAt: post.created_at
                      }}
                      onLike={handleLike}
                      onSave={handleSave}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <UserIcon size={48} className="mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {isOwnProfile
                      ? select({ es: 'No has publicado nada aún', en: 'You have not posted anything yet' })
                      : select({ es: 'No hay publicaciones', en: 'No posts available' })}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {isOwnProfile
                      ? select({ es: 'Comparte tu primera obra de arte con la comunidad', en: 'Share your first artwork with the community' })
                      : select({ es: 'Este usuario no ha compartido ninguna obra aún', en: 'This user has not shared any artwork yet' })}
                  </p>
                  {isOwnProfile && (
                    <button
                      onClick={onCreatePost}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                    >
                      {select({ es: 'Crear primera publicación', en: 'Create first post' })}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Collections Tab */}
          {activeTab === 'collections' && isOwnProfile && (
            <div>
              {collections.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {collections.map(collection => (
                    <div key={collection.id} className="bg-gray-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <h3 className="font-semibold text-gray-900 mb-2">{collection.name}</h3>
                      {collection.description && (
                        <p className="text-gray-600 text-sm mb-3">{collection.description}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          {collection.collection_posts?.length || 0}{' '}
                          {select({
                            es: (collection.collection_posts?.length || 0) === 1 ? 'obra' : 'obras',
                            en: (collection.collection_posts?.length || 0) === 1 ? 'work' : 'works',
                          })}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${collection.is_public
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                          }`}>
                          {collection.is_public
                            ? select({ es: 'Pública', en: 'Public' })
                            : select({ es: 'Privada', en: 'Private' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Bookmark size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {select({ es: 'No tienes colecciones aún', en: 'You have no collections yet' })}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {select({ es: 'Organiza tus obras favoritas en colecciones', en: 'Organize your favorite works into collections' })}
                  </p>
                  <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                    {select({ es: 'Crear colección', en: 'Create collection' })}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && isOwnProfile && (
            <div>
              {orders.length > 0 ? (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4 md:p-6">
                        <div className="flex flex-col md:flex-row gap-6">
                          {/* Image */}
                          <div className="w-full md:w-32 h-32 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                             {order.post?.media_files?.[0] ? (
                               <img 
                                 src={order.post.media_files[0].url} 
                                 alt={order.post.title} 
                                 className="w-full h-full object-cover"
                               />
                             ) : (
                               <div className="w-full h-full flex items-center justify-center text-gray-400">
                                 <ShoppingBag size={24} />
                               </div>
                             )}
                          </div>
                          
                          {/* Details */}
                          <div className="flex-1">
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                              <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-1">{order.post?.title || 'Obra eliminada'}</h3>
                                <p className="text-sm text-gray-500 mb-2">
                                  {select({ es: 'Vendido por', en: 'Sold by' })} @{order.seller?.username}
                                </p>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Calendar size={14} />
                                  <span>{new Date(order.created_at).toLocaleDateString(locale)}</span>
                                </div>
                              </div>
                              
                              <div className="text-right">
                                <p className="text-2xl font-bold text-gray-900">
                                  {new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(order.final_price || order.sale_price || 0)}
                                </p>
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium mt-2
                                  ${(order.delivery_status === 'delivered' || order.status === 'completed') ? 'bg-green-100 text-green-800' : 
                                    (order.delivery_status === 'shipped' || order.status === 'processing') ? 'bg-blue-100 text-blue-800' : 
                                    'bg-yellow-100 text-yellow-800'}`}>
                                  {(order.delivery_status === 'delivered' || order.status === 'completed') ? <CheckCircle size={12} /> : 
                                   (order.delivery_status === 'shipped' || order.status === 'processing') ? <Truck size={12} /> : 
                                   <ShoppingBag size={12} />}
                                  {select({
                                    es: (order.delivery_status === 'delivered' || order.status === 'completed') ? 'Completado' : 
                                        (order.delivery_status === 'shipped' || order.status === 'processing') ? 'Procesando' : 'Pendiente',
                                    en: (order.delivery_status === 'delivered' || order.status === 'completed') ? 'Completed' : 
                                        (order.delivery_status === 'shipped' || order.status === 'processing') ? 'Processing' : 'Pending'
                                  })}
                                </span>
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="pt-4 border-t border-gray-100 flex flex-wrap gap-3">
                              <button 
                                onClick={() => navigate(`/post/${order.post?.id}`)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                {select({ es: 'Ver obra', en: 'View artwork' })}
                              </button>
                              
                              {(order.payment_status === 'paid' || order.status === 'completed' || order.status === 'processing') && order.post?.media_files?.[0]?.url && (
                                <a 
                                  href={order.post.media_files[0].url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-2"
                                >
                                  <Download size={16} />
                                  {select({ es: 'Descargar obra', en: 'Download artwork' })}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ShoppingBag size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {select({ es: 'No has realizado pedidos aún', en: 'You haven\'t placed any orders yet' })}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {select({ es: 'Explora nuestra galería y encuentra obras únicas', en: 'Explore our gallery and find unique artworks' })}
                  </p>
                  <button 
                    onClick={() => navigate('/')}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {select({ es: 'Explorar arte', en: 'Explore art' })}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Followers Tab */}
          {activeTab === 'followers' && (
            <div>
              {followers && followers.length > 0 ? (
                <div className="space-y-4">
                  {followers.filter(follower => follower && follower.id).map(follower => (
                    <div key={follower.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <img
                        src={follower.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=48&h=48&fit=crop'}
                        alt={follower.full_name || select({ es: 'Usuario', en: 'User' })}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {follower.full_name || select({ es: 'Usuario desconocido', en: 'Unknown user' })}
                        </h3>
                        <p className="text-gray-600">
                          @{follower.username || select({ es: 'usuario', en: 'user' })}
                        </p>
                        {follower.bio && (
                          <p className="text-gray-500 text-sm mt-1 line-clamp-2">{follower.bio}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <FollowButton
                          userId={follower.id}
                          username={follower.username || ''}
                          size="sm"
                          variant="outline"
                        />
                        <button className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors">
                          {select({ es: 'Ver perfil', en: 'View profile' })}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {isOwnProfile
                      ? select({ es: 'No tienes seguidores aún', en: 'You do not have followers yet' })
                      : select({ es: 'No tiene seguidores', en: 'No followers' })}
                  </h3>
                  <p className="text-gray-500">
                    {isOwnProfile
                      ? select({ es: 'Comparte contenido increíble para atraer seguidores', en: 'Share amazing content to attract followers' })
                      : select({ es: 'Este usuario no tiene seguidores aún', en: 'This user does not have followers yet' })}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Following Tab */}
          {activeTab === 'following' && isOwnProfile && (
            <div>
              {following && following.length > 0 ? (
                <div className="space-y-4">
                  {following.filter(followedUser => followedUser && followedUser.id).map(followedUser => (
                    <div key={followedUser.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <img
                        src={followedUser.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=48&h=48&fit=crop'}
                        alt={followedUser.full_name || select({ es: 'Usuario', en: 'User' })}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{followedUser.full_name || select({ es: 'Usuario desconocido', en: 'Unknown user' })}</h3>
                        <p className="text-gray-600">@{followedUser.username || select({ es: 'usuario', en: 'user' })}</p>
                        {followedUser.bio && (
                          <p className="text-gray-500 text-sm mt-1 line-clamp-2">{followedUser.bio}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <FollowButton
                          userId={followedUser.id}
                          username={followedUser.username || ''}
                          size="sm"
                          variant="outline"
                        />
                        <button className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors">
                          {select({ es: 'Ver perfil', en: 'View profile' })}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {select({ es: 'No sigues a nadie aún', en: 'You are not following anyone yet' })}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {select({ es: 'Explora artistas y empieza a seguir a quienes te inspiren', en: 'Explore artists and start following those who inspire you' })}
                  </p>
                  <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                    {select({ es: 'Descubrir artistas', en: 'Discover artists' })}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ReportDialog
        isOpen={showReportModal}
        title={select({ es: 'Reportar perfil', en: 'Report profile' })}
        itemDescription={`@${profileUser.username}`}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleReportProfileSubmit}
      />

      {/* Settings Modal */}
      {showSettingsModal && (
        <SettingsModal
          isOpen={showSettingsModal}
          initialTab="account"
          onClose={() => setShowSettingsModal(false)}
        />
      )}
    </div>
  );
};

export default ProfileView;
